import React from 'react';
import PropTypes from 'prop-types';

/**
 * QuickSettings - Inline settings for Simple Workflow
 * Compact version of GenerationSettingsSection
 */
function QuickSettings({
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  multipleChoiceMode = 'single', // 'single', 'multiple', 'mixed'
  setMultipleChoiceMode,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  compact = false
}) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 ${compact ? 'p-3' : 'p-4'}`}>
      <h3 className={`font-semibold text-gray-900 dark:text-white ${compact ? 'text-sm mb-3' : 'mb-4'}`}>
        Quick Settings
      </h3>
      
      {/* Row 1: Basic Settings */}
      <div className={`grid ${compact ? 'grid-cols-3 gap-3' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
        {/* Number of Questions */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
            Anzahl Fragen
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setNumberOfQuestions(Math.max(1, numberOfQuestions - 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max="50"
              value={numberOfQuestions}
              onChange={(e) => setNumberOfQuestions(parseInt(e.target.value) || 5)}
              className="w-14 h-8 text-center border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <button
              onClick={() => setNumberOfQuestions(Math.min(50, numberOfQuestions + 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              +
            </button>
          </div>
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
            Schwierigkeit
          </label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            className="w-full h-8 px-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="easy">Einfach</option>
            <option value="medium">Mittel</option>
            <option value="hard">Schwer</option>
            <option value="mixed">Gemischt</option>
          </select>
        </div>

        {/* Multiple Choice Mode */}
        {setMultipleChoiceMode && (
          <div>
            <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
              Antworttyp
            </label>
            <select
              value={multipleChoiceMode}
              onChange={(e) => setMultipleChoiceMode(e.target.value)}
              className="w-full h-8 px-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="single">Single Choice</option>
              <option value="multiple">Multiple Choice</option>
              <option value="mixed">Gemischt</option>
            </select>
          </div>
        )}
      </div>

      {/* Row 2: Code Toggle - Full width button */}
      <div className="mt-4">
        <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
          Code-Beispiele
        </label>
        <button
          onClick={() => setIncludeCodeBlocks(!includeCodeBlocks)}
          className={`
            w-full h-10 px-4 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2
            ${includeCodeBlocks 
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }
          `}
        >
          {includeCodeBlocks ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Mit Code-Beispielen
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Ohne Code (nur Text)
            </>
          )}
        </button>
      </div>

      {/* User Guidance - Only in non-compact mode */}
      {!compact && (
        <div className="mt-4">
          <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
            Thema / Anweisungen <span className="text-amber-500">(wichtig ohne Dokument!)</span>
          </label>
          <textarea
            value={userGuidance}
            onChange={(e) => setUserGuidance(e.target.value)}
            placeholder="WICHTIG: Beschreibe das Thema wenn kein Dokument hochgeladen ist!

Beispiele:
• 'Erstelle Fragen zu Python List Comprehensions und Generatoren'
• 'SQL JOIN-Typen: INNER, LEFT, RIGHT, FULL OUTER'
• 'JavaScript async/await und Promise-Handling'
• 'React Hooks: useState, useEffect, useContext'"
            rows={4}
            className="w-full p-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400"
          />
        </div>
      )}

      {/* Topic Tags */}
      <div className="mt-4">
        <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">
          Themen-Tags
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={currentTag}
            onChange={(e) => setCurrentTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddTopicTag();
              }
            }}
            placeholder="Tag eingeben + Enter"
            className="flex-1 h-8 px-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
          />
          <button
            onClick={onAddTopicTag}
            disabled={!currentTag.trim()}
            className="px-3 h-8 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 text-sm font-medium disabled:opacity-50"
          >
            +
          </button>
        </div>
        
        {topicTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {topicTags.map((tag, idx) => (
              <span
                key={idx}
                className="px-2.5 py-1 bg-secondary-100 dark:bg-secondary-900/50 text-secondary-700 dark:text-secondary-300 rounded-full text-xs flex items-center gap-1"
              >
                {tag}
                <button
                  onClick={() => onRemoveTopicTag(tag)}
                  className="text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 font-bold ml-0.5"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

QuickSettings.propTypes = {
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  multipleChoiceMode: PropTypes.oneOf(['single', 'multiple', 'mixed']),
  setMultipleChoiceMode: PropTypes.func,
  userGuidance: PropTypes.string,
  setUserGuidance: PropTypes.func,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  compact: PropTypes.bool
};

export default QuickSettings;
